import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import {useState, useMemo, useEffect, useContext, useCallback} from 'react';
import { observer } from 'mobx-react-lite';
import Fuse from 'fuse.js';

import './products.css';
import { Context } from '../../../../index';
import ProductModal from '../../../Modal/ProductModal';
import NotFound from '../../../Other/NotFound';
import Search from '../../../Other/Search';
import SpinnerLoader from '../../../Loaders/SpinnerLoader';
import { CartIcon, DiscountShape } from '../../../Other/Icons';

function ServerButton({ name, isActive, onClick }:any) {
    return (
        <button className={`button-3 chooseServer__server ${isActive ? 'active':''}`} onClick={onClick}>
            <div className="chooseServer__serverName">{name}</div> 
        </button>
    );
}

const Item = ({product, setImageLoaded}:any) => {
    const priceFormatter = useMemo(() => new Intl.NumberFormat('ru-RU'), []);
    const {store} = useContext(Context)
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();
    const languages = i18n.languages;
    const {img, nameRU, nameEN, price, newPrice, qnty, discount, type, plans} = product,
        qntyComp = new Intl.NumberFormat('ru-RU').format(qnty),
        priceOld = discount > 0 && 
            <div className='product__price-old'>
                <span className='line-through'>{priceFormatter.format(price)}</span>
                <span> RUB</span>
            </div>,
        priceComp = newPrice ? `${priceFormatter.format(Math.floor(newPrice))} RUB` : t('defaults.free')

    const {id, imageLoaded} = product ?? {};

    return (
        <div className={`product type-${type}`} onClick={e => navigate(`/products/${product.product_id}`)}>
            <div className={`product_body ${!imageLoaded ? 'loading':''}`}>
                <div className="product__name_wrap">
                    <div className="product__name">{(languages.includes('ru') ? nameRU: nameEN) }</div>
                </div>
                <div className="product_image_wrap">
                    <img 
                        className="product__image" 
                        src={store.resizeImg(`${img}.png`)} alt=""
                        onLoad={() => setImageLoaded(id)}
                        style={{
                            opacity: imageLoaded ? 1: 0,
                        }}
                    />
                </div>
                {qnty > 1 && <div className="product__qnty">x{qntyComp}</div>}
                {   discount > 0 && 
                    <div className="product__discount">
                        <div className="discount_wrap">
                            <DiscountShape />
                            <span>-{discount}%</span>
                        </div>
                    </div>
                }
            </div>
            <div className="product_footer">
                {   price > 0  &&
                    <>
                        <div className={`product__price ${discount > 0 ? 'discount' : ''}`}>
                            {plans.length>0 ? t('defaults.from') : ''} {priceComp}
                            {priceOld}
                        </div>
                        <div className="product__buy button">
                            <CartIcon />
                            {t('defaults.buy')}
                        </div>
                    </>
                }
                {   price === 0  &&
                    <div className="product__buy button flex-1 free">
                        <CartIcon />
                        {t('defaults.receive')}
                    </div>
                }
            </div>
        </div>
    )
}

const ServerChoose = ({serverCategory, setServerCategory}:any) => {
    return (
        <div className='chooseServer'>
            <nav className="chooseServer__list">
                <ServerButton name="MODDED X5" isActive={serverCategory==='modded_x5'} onClick={() => setServerCategory('modded_x5')}/>
                <ServerButton name="SEMI-CLASSIC" isActive={serverCategory==='semi-classic'} onClick={() => setServerCategory('semi-classic')}/>
            </nav>
        </div>
    )
}

const renderColumn = (items: any[], cols: number, setImageLoaded:Function) => (
    items.length > 0 && (
        <div className={`grid gap-1em cols-${cols}`}>
            {items.map((product: any) => (
                <Item product={product} key={product?.product_id} setImageLoaded={setImageLoaded} />
            ))}
        </div>
    )
);

const Items = () => {
    const priceFormatter = useMemo(() => new Intl.NumberFormat('ru-RU'), []);
    const navigate = useNavigate();
    const {store} = useContext(Context),
    {isProductsLoaded} = store,
    {id} = useParams(),
    [globalFilter, setGlobalFilter]  = useState<string>(''),
    [globalStartPrice, setGlobalStartPrice] = useState<number>(0),
    [globalFinishPrice, setGlobalFinishPrice] = useState<number>(0),
    [modal, setModal] = useState(false),
    [currProduct, setCurrProduct] = useState<object>(null!),
    [serverCategory, setServerCategory]  = useState('modded_x5');
    const { t } = useTranslation();
    const [products, setProducts] = useState<any[]>([]);
    const [productsFiltered, setProductsFiltered] = useState<any[]>([]);
    const [localProductsLoaded, setLocalProductsLoaded] = useState(false);

    function clearFilter() {
        setGlobalFilter('')
        setGlobalStartPrice(0)
        setGlobalFinishPrice(0)
    }

    function openProduct(product:any) {
        setCurrProduct(product)
        setModal(true)
    }
    
    useEffect(() => {
        store.getProducts()
    }, [])

    useEffect(() => {
        if (isProductsLoaded) {
            setProducts(store?.products);
        }
    }, [isProductsLoaded])

    useEffect(() => {
        if (localProductsLoaded) {
            setCurrProduct(products[0]);
        }
    }, [localProductsLoaded])
    
    useEffect(() => {
        if (!id) return;
        if (localProductsLoaded) {
            const product = products?.find((prod: any) => prod.product_id == id);
            if (product) openProduct(product)
            else navigate('/products')
        }
    }, [id, localProductsLoaded])

    useEffect(() => {
        if (isProductsLoaded) {
            setProductsFiltered(products.filter((prod:any) => prod.servers === serverCategory));
            setLocalProductsLoaded(true);
        }
    }, [products])

    useEffect(() => {
        if (localProductsLoaded) {
            setProductsFiltered(products.filter((prod:any) => prod.servers === serverCategory));
        }
    }, [serverCategory])

    const fuse = new Fuse(productsFiltered, {
        keys: ['nameRU', 'nameEN', 'shortname'],
        threshold: .4,
    });
    const results = fuse.search(globalFilter);
    const findResults = globalFilter ? results?.map((res) => res.item) : productsFiltered;
    const allFindResults = findResults.filter((res:any) => ( 
        ((res.plans && res.plans.length > 0 && res.plans[res.plans.length - 1].newPrice >= globalStartPrice) || res.newPrice >= globalStartPrice) 
        && ( !globalFinishPrice || (res.plans && res.plans.length > 0 && res.plans[0].newPrice <= globalFinishPrice) || res.newPrice <= globalFinishPrice)
    ))

    const searchEnabled = globalFilter || globalFinishPrice || globalStartPrice;
    const columnItems4 = allFindResults?.slice(0, 4);
    const columnItems5 = !searchEnabled ? allFindResults?.slice(4, 9) : allFindResults;
    const columnItems6 = allFindResults?.slice(9);

    const setImageLoaded = useCallback((id: string) => {
        setProducts(prev => prev.map((p: any) =>
            p.id === id ? { ...p, imageLoaded: true } : p
        ));
    }, []);

    return (
        <div className="Products">
            {currProduct && <ProductModal open={modal} setOpen={setModal} product={currProduct}/>}
            <ServerChoose serverCategory={serverCategory} setServerCategory={setServerCategory} />
            <div className='products__Wrapper'>
                <div className='listProducts'>
                    <div className="searchbox">
                        <div className="search__wrapper">
                            <Search 
                                class='searchProducts'
                                placeholder={t('main.search.supposeText')}
                                value={globalFilter}
                                onChange={(e:string) => setGlobalFilter(e)}
                            />
                            <div className="search-1 filter_amountFields">
                                <div className="filter_amountLabel">
                                    <div className="filter_currency">₽</div>
                                    <input type="text" className="filter_amountInput" id='main_filter_amountInput-from' placeholder={t('main.search.from')} autoComplete="off" value={globalStartPrice!=0 ? priceFormatter.format(globalStartPrice): ''} onChange={e => setGlobalStartPrice(Number(e.target.value.replace(/[^0-9]/g,'')))} maxLength={6}/>
                                </div>
                                <div className="filter_amountLabel">
                                    <div className="filter_currency">₽</div>
                                    <input type="text" className="filter_amountInput" id='main_filter_amountInput-to' placeholder={t('main.search.to')} autoComplete="off" value={globalFinishPrice!=0 ? priceFormatter.format(globalFinishPrice): ''} onChange={e => setGlobalFinishPrice(Number(e.target.value.replace(/[^0-9]/g,'')))} maxLength={6}/>
                                </div>
                                <div className="filter_amountSeparator"></div>
                            </div>
                        </div>
                        <button className="button-1 filter_clearBtn" onClick={e => {e.currentTarget.blur(); clearFilter();}}>
                            <svg className="clearBtn__ico" fill="none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path fillRule="evenodd" clipRule="evenodd" d="M8.188 3.322c.523-.21 1.1-.266 1.656-.158a2.894 2.894 0 0 1 1.468.762l.908.877 2.994-2.909c.237-.222.554-.344.883-.342.329.003.644.131.876.357.233.226.365.531.368.85a1.2 1.2 0 0 1-.352.858l-2.997 2.906.906.877c.4.39.673.886.784 1.426.11.54.053 1.1-.164 1.608l-.652 1.53-8.252-8.009 1.574-.633ZM4.943 4.63l9.227 8.959-1.448 3.391c-.07.163-.18.307-.32.419a1.12 1.12 0 0 1-1.021.196 1.11 1.11 0 0 1-.46-.269L1.093 7.783a1.042 1.042 0 0 1-.073-1.436c.115-.137.262-.243.43-.311L4.942 4.63ZM17.49 17.243c0 .673.562 1.219 1.255 1.219.693 0 1.255-.546 1.255-1.219 0-.672-.562-1.218-1.255-1.218-.693 0-1.255.546-1.255 1.218Zm-1.673-.407a.824.824 0 0 1-.836-.81c0-.449.374-.812.836-.812.461 0 .835.363.835.811a.824.824 0 0 1-.835.811Zm1.674-3.247c0 .448.374.811.835.811a.824.824 0 0 0 .836-.811.824.824 0 0 0-.836-.812.824.824 0 0 0-.835.812Z" fill="#c8c8c8"></path></svg>
                            {t('main.search.clear')}
                        </button>
                    </div>
                </div>
                {   isProductsLoaded && localProductsLoaded ?
                        allFindResults.length > 0 ?
                        <div className={`store_products ${searchEnabled ? 'search-enabled' : ''}`}>
                            {!searchEnabled && renderColumn(columnItems4, 4, setImageLoaded)}
                            {renderColumn(columnItems5, 5, setImageLoaded)}
                            {!searchEnabled && renderColumn(columnItems6, 6, setImageLoaded)}
                        </div>
                        :
                        <div className="product_notfound_wrap">
                            <NotFound subtitle={t('main.products.not_found.subtitle')}/>
                        </div>
                    :
                    <SpinnerLoader />
                }
            </div>
        </div>
    )
}

export default observer(Items)